import React, { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '../../../stores/ThemeContext';

const VIEWS = {
  LAST_30: 'last30',
  LAST_90: 'last90',
};

const CustomTooltip = ({ active, payload, label, isDark }) => {
  if (!active || !payload?.length) return null;
  const tooltipBg     = isDark ? '#1c1c1e' : '#ffffff';
  const tooltipBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.12)';
  const tooltipText   = isDark ? '#fafafa' : '#111111';
  return (
    <div
      style={{
        backgroundColor: tooltipBg,
        border: tooltipBorder,
        borderRadius: 8,
        padding: '8px 12px',
        fontSize: 12,
        color: tooltipText,
      }}
    >
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {p.value.toFixed(1)}
        </p>
      ))}
    </div>
  );
};

const ScoreTrends = ({ data }) => {
  const [view, setView] = useState(VIEWS.LAST_30);
  const { isDark } = useTheme();

  const gridColor   = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const axisColor   = isDark ? '#a1a1aa' : '#52525b';
  const legendColor = isDark ? '#a1a1aa' : '#52525b';

  const chartData = useMemo(() => {
    const entries = data?.score_entries || [];
    if (entries.length === 0) return [];

    const scaled = entries.map(e => ({
      date: new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      rawDate: e.date,
      emotion: Math.round((e.emotion / 10) * 10) / 10,
      risk: Math.round((e.risk / 10) * 10) / 10,
      strategy: Math.round((e.strategy / 10) * 10) / 10,
    }));

    if (view === VIEWS.LAST_30) {
      return scaled.slice(-30);
    }

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 90);
    const cutoffStr = cutoff.toISOString().substring(0, 10);
    return scaled.filter(e => e.rawDate >= cutoffStr);
  }, [data, view]);

  if (!data?.score_entries?.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h3 className="text-sm font-semibold text-foreground mb-1">Score Trends Over Time</h3>
        <p className="text-xs text-muted-foreground mb-4">Emotion, Risk & Strategy trends</p>
        <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
          No scored journals yet. Complete journals with scores to see trends.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-1">Score Trends Over Time</h3>
          <p className="text-xs text-muted-foreground">Emotion, Risk & Strategy trends</p>
        </div>
        <div className="flex gap-1 bg-background rounded-md p-0.5 border border-border">
          <button
            onClick={() => setView(VIEWS.LAST_30)}
            className={`px-3 py-1 text-xs rounded transition-colors ${
              view === VIEWS.LAST_30
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Last 30 Entries
          </button>
          <button
            onClick={() => setView(VIEWS.LAST_90)}
            className={`px-3 py-1 text-xs rounded transition-colors ${
              view === VIEWS.LAST_90
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Last 90 Days
          </button>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <ReferenceArea
            y1={1}
            y2={4}
            fill="#ef4444"
            fillOpacity={0.06}
            label={{ value: 'Danger Zone', position: 'insideTopLeft', fill: '#ef444480', fontSize: 10 }}
          />
          <ReferenceArea
            y1={7}
            y2={10}
            fill="#22c55e"
            fillOpacity={0.06}
            label={{ value: 'Strong Zone', position: 'insideBottomLeft', fill: '#22c55e80', fontSize: 10 }}
          />
          <XAxis
            dataKey="date"
            tick={{ fill: axisColor, fontSize: 10 }}
            angle={-45}
            textAnchor="end"
            height={60}
            interval="preserveStartEnd"
          />
          <YAxis
            domain={[1, 10]}
            ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
            tick={{ fill: axisColor, fontSize: 11 }}
          />
          <Tooltip content={<CustomTooltip isDark={isDark} />} />
          <Legend
            wrapperStyle={{ fontSize: 11, color: legendColor, paddingTop: 8 }}
          />
          <Line
            type="monotone"
            dataKey="emotion"
            name="Emotion"
            stroke="#8b5cf6"
            strokeWidth={2}
            dot={{ r: 3, fill: '#8b5cf6' }}
            activeDot={{ r: 5 }}
          />
          <Line
            type="monotone"
            dataKey="risk"
            name="Risk"
            stroke="#f59e0b"
            strokeWidth={2}
            dot={{ r: 3, fill: '#f59e0b' }}
            activeDot={{ r: 5 }}
          />
          <Line
            type="monotone"
            dataKey="strategy"
            name="Strategy"
            stroke="#14b8a6"
            strokeWidth={2}
            dot={{ r: 3, fill: '#14b8a6' }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ScoreTrends;
